import React, { useEffect } from 'react';

// Formik
import { FormikProps } from 'formik';

// Debounce
import { useDebouncedCallback } from 'use-debounce';

// Yup
import { reach } from 'yup';
import { addressSchema } from 'yupSchemas/addressSchema';

// Material
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

// Helpers
import { useDebouncedSearch } from 'helpers/debounceHook';
import { Here } from 'helpers/geocoding';

// Services
import searchCep from 'services/apiSearchCep';

// Components
import { MaskedInputCEP } from 'components/MaskedInputs';

// Constants
import STATE_LIST from 'constants/states';

// Styles
import useStyles from './formAddress.styles';

export interface NewAddressInterface {
  cep: string;
  street: string;
  number: string;
  compl: string;
  neigh: string;
  city: string;
  state: string;
}

interface NewAddressFormProps {
  formik: FormikProps<any>;
  className?: string;
  fieldName?: string;
  defaultStyle?: boolean;
  disabled?: boolean;
  withMargin?: boolean;
  textAlign?: boolean;
  spacing?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
  notLabel?: boolean;
  inputTitle?: boolean;
}

type Props = NewAddressFormProps;

const useSearchCep = () => useDebouncedSearch(searchCep);

const NewAddressForm: React.FC<Props> = ({
  formik,
  className,
  fieldName,
  disabled,
  withMargin,
  textAlign,
  spacing,
  defaultStyle,
  notLabel,
  inputTitle,
}) => {
  const classes = useStyles();
  const [_, setCepFieldText, searchResults] = useSearchCep();

  const classStyle = defaultStyle
    ? classes.defaultStyle
    : (className && className) || classes.input;

  const cepIsValid = async (cep: string) => {
    const valid = await reach(addressSchema, 'cep').isValid(cep);
    return valid;
  };
  const handleChangeCep = async (event: any) => {
    formik.handleChange(event);
    const valid = await cepIsValid(event.target.value);

    if (valid) {
      debouncedCallbackLatLon(event.target.value);
      setCepFieldText(event.target.value);
    }
  };

  const [debouncedCallbackLatLon] = useDebouncedCallback((value: string) => {
    if (value.substring(value.length - 3) === '000') {
      Here.autosuggest(value)
        .then(({ items }) => {
          if (items.length > 0) {
            const lat = items[0]?.position?.lat;
            const lng = items[0]?.position?.lng;
            formik.setFieldValue('latitude', lat);
            formik.setFieldValue('longitude', lng);
            formik.setFieldValue('lat', lat);
            formik.setFieldValue('long', lng);
            formik.setFieldValue('startLat', lat);
            formik.setFieldValue('startLong', lng);
            formik.setFieldValue('endLat', lat);
            formik.setFieldValue('endLong', lng);
          }
        })
        .catch((error: any) => {
          console.error('error', error);
        });
    }
  }, 800);

  const setAddressData = (data: any) => {
    const { bairro, cidade, endereco, uf, cep } = data;
    if (fieldName) {
      formik.setFieldValue('address', {
        ...formik.values.address,
        state: uf,
        city: cidade,
        neigh: bairro,
        street: endereco,
        cep,
      });
    } else {
      formik.setFieldValue('cep', cep);
      formik.setFieldValue('street', endereco);
      formik.setFieldValue('neigh', bairro);
      formik.setFieldValue('city', cidade);
      formik.setFieldValue('state', uf);
    }
  };

  useEffect(() => {
    if (!searchResults.loading && searchResults.result) {
      const { status, data } = searchResults.result;
      if (status === 200 && !data.error) {
        setAddressData(data);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResults.result, searchResults.loading]);

  return (
    <Grid container direction='row' spacing={spacing}>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        {inputTitle && (
          <Typography
            style={{
              color: '#00CAD7',
              padding: '0.25rem 0.75rem',
            }}>
            CEP
          </Typography>
        )}
        <TextField
          disabled={disabled}
          label={notLabel ? '' : 'CEP'}
          placeholder={notLabel ? 'CEP' : ''}
          className={classStyle}
          InputLabelProps={
            textAlign
              ? {
                  style: {
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',

                    marginTop: -5,
                  },
                  shrink: fieldName
                    ? formik.values.address.cep
                    : formik.values.cep,
                }
              : {
                  shrink: fieldName
                    ? formik.values.address.cep
                    : formik.values.cep,
                }
          }
          name={fieldName ? `${fieldName}.cep` : 'cep'}
          fullWidth
          variant='outlined'
          value={fieldName ? formik.values.address.cep : formik.values.cep}
          onBlur={formik.handleBlur}
          onChange={handleChangeCep}
          margin={withMargin ? 'normal' : 'none'}
          InputProps={{ inputComponent: MaskedInputCEP as any }}
          error={Boolean(
            formik.touched &&
              formik.errors &&
              (formik.errors.cep ||
                (formik.errors.address as { cep: string })?.cep) &&
              (formik.touched.cep ||
                ((formik.touched?.address as unknown) as { cep: string })?.cep),
          )}
          helperText={
            formik.touched &&
            formik.errors &&
            (formik.errors.cep ||
              (formik.errors.address as { cep: string })?.cep) &&
            (formik.touched.cep ||
              ((formik.touched?.address as unknown) as { cep: string })?.cep) &&
            (formik.errors.cep ||
              (formik.errors.address as { cep: string })?.cep)
          }
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        {inputTitle && (
          <Typography
            style={{
              color: '#00CAD7',
              padding: '0.25rem 0.75rem',
            }}>
            Rua
          </Typography>
        )}
        <TextField
          label={notLabel ? '' : 'Rua'}
          placeholder={notLabel ? 'Rua' : ''}
          disabled={disabled}
          className={classStyle}
          InputLabelProps={
            textAlign
              ? {
                  style: {
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',

                    marginTop: -5,
                  },
                  shrink: fieldName
                    ? formik.values.address.street
                    : formik.values.street,
                }
              : {
                  shrink: fieldName
                    ? formik.values.address.street
                    : formik.values.street,
                }
          }
          name={fieldName ? `${fieldName}.street` : 'street'}
          fullWidth
          variant='outlined'
          value={
            fieldName ? formik.values.address.street : formik.values.street
          }
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          margin={withMargin ? 'normal' : 'none'}
          error={Boolean(
            formik.touched &&
              formik.errors &&
              formik.errors.street &&
              formik.touched.street,
          )}
          helperText={
            formik.touched &&
            formik.errors &&
            formik.errors.street &&
            formik.touched.street &&
            formik.errors.street
          }
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        {inputTitle && (
          <Typography
            style={{
              color: '#00CAD7',
              padding: '0.25rem 0.75rem',
            }}>
            Número
          </Typography>
        )}
        <TextField
          classes={{
            root: classes.inputRoot,
          }}
          className={classStyle}
          label={notLabel ? '' : 'Número'}
          placeholder={notLabel ? 'Número' : ''}
          InputLabelProps={
            textAlign
              ? {
                  style: {
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',

                    marginTop: -5,
                  },
                  shrink: fieldName
                    ? formik.values.address.number
                    : formik.values.number,
                }
              : {
                  shrink: fieldName
                    ? formik.values.address.number
                    : formik.values.number,
                }
          }
          disabled={disabled}
          name={fieldName ? `${fieldName}.number` : 'number'}
          fullWidth
          variant='outlined'
          type='number'
          value={
            fieldName ? formik.values.address.number : formik.values.number
          }
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          margin={withMargin ? 'normal' : 'none'}
          error={Boolean(
            formik.touched &&
              formik.errors &&
              (formik.errors?.number ||
                (formik.errors?.address as { number: string })?.number) &&
              (formik.touched?.number ||
                ((formik.touched?.address as unknown) as { number: string })
                  ?.number),
          )}
          helperText={
            formik.touched &&
            formik.errors &&
            (formik.errors.number ||
              (formik.errors.address as { number: string })?.number) &&
            (formik.touched.number ||
              ((formik.touched?.address as unknown) as { number: string })
                ?.number) &&
            (formik.errors.number ||
              (formik.errors.address as { number: string })?.number)
          }
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        {inputTitle && (
          <Typography
            style={{
              color: '#00CAD7',
              padding: '0.25rem 0.75rem',
            }}>
            Complemento
          </Typography>
        )}
        <TextField
          disabled={disabled}
          label={notLabel ? '' : 'Complemento'}
          placeholder={notLabel ? 'Complemento' : ''}
          className={classStyle}
          InputLabelProps={
            textAlign
              ? {
                  style: {
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',

                    marginTop: -5,
                  },
                  shrink: fieldName
                    ? formik.values.address.compl
                    : formik.values.compl,
                }
              : {
                  shrink: fieldName
                    ? formik.values.address.compl
                    : formik.values.compl,
                }
          }
          name={fieldName ? `${fieldName}.compl` : 'compl'}
          fullWidth
          variant='outlined'
          margin={withMargin ? 'normal' : 'none'}
          value={fieldName ? formik.values.address.compl : formik.values.compl}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        {inputTitle && (
          <Typography
            style={{
              color: '#00CAD7',
              padding: '0.25rem 0.75rem',
            }}>
            Bairro
          </Typography>
        )}
        <TextField
          disabled={disabled}
          label={notLabel ? '' : 'Bairro'}
          placeholder={notLabel ? 'Bairro' : ''}
          className={classStyle}
          InputLabelProps={
            textAlign
              ? {
                  style: {
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',

                    marginTop: -5,
                  },
                  shrink: fieldName
                    ? formik.values.address.neigh
                    : formik.values.neigh,
                }
              : {
                  shrink: fieldName
                    ? formik.values.address.neigh
                    : formik.values.neigh,
                }
          }
          name={fieldName ? `${fieldName}.neigh` : 'neigh'}
          fullWidth
          variant='outlined'
          margin={withMargin ? 'normal' : 'none'}
          value={fieldName ? formik.values.address.neigh : formik.values.neigh}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        {inputTitle && (
          <Typography
            style={{
              color: '#00CAD7',
              padding: '0.25rem 0.75rem',
            }}>
            Cidade
          </Typography>
        )}
        <TextField
          disabled={disabled}
          label={notLabel ? '' : 'Cidade'}
          placeholder={notLabel ? 'Cidade' : ''}
          className={classStyle}
          InputLabelProps={
            textAlign
              ? {
                  style: {
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',

                    marginTop: -5,
                  },
                  shrink: fieldName
                    ? formik.values.address.city
                    : formik.values.city,
                }
              : {
                  shrink: fieldName
                    ? formik.values.address.city
                    : formik.values.city,
                }
          }
          name={fieldName ? `${fieldName}.city` : 'city'}
          fullWidth
          variant='outlined'
          margin={withMargin ? 'normal' : 'none'}
          value={fieldName ? formik.values.address.city : formik.values.city}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        {inputTitle && (
          <Typography
            style={{
              color: '#00CAD7',
              padding: '0.25rem 0.75rem',
            }}>
            Estado
          </Typography>
        )}
        <TextField
          disabled={disabled}
          label={notLabel ? '' : 'Estado'}
          placeholder={notLabel ? 'Estado' : ''}
          className={classStyle}
          InputLabelProps={
            textAlign
              ? {
                  style: {
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',

                    marginTop: -5,
                  },
                  shrink: fieldName
                    ? formik.values.address.state
                    : formik.values.state,
                }
              : {
                  shrink: fieldName
                    ? formik.values.address.state
                    : formik.values.state,
                }
          }
          name={fieldName ? `${fieldName}.state` : 'state'}
          select
          fullWidth
          variant='outlined'
          value={fieldName ? formik.values.address.state : formik.values.state}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          margin={withMargin ? 'normal' : 'none'}
          error={Boolean(
            formik.touched &&
              formik.errors &&
              formik.errors.state &&
              formik.touched.state,
          )}
          helperText={
            formik.touched &&
            formik.errors &&
            formik.errors.state &&
            formik.touched.state &&
            formik.errors.state
          }>
          {STATE_LIST.map((state) => (
            <MenuItem key={state.abbreviation} value={state.abbreviation}>
              {state.abbreviation}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
    </Grid>
  );
};

export { NewAddressForm };
export default NewAddressForm;
